// The one approved deployment this build was made for (docs/18-one-fee-reserve.md
// 2.5). The entry is chosen by name at build time (`VITE_DEPLOYMENT`, default
// `mainnet`; vite.config.ts refuses any other name and any `VITE_PROGRAM_ID`)
// from the SDK's table (sdk/ts/src/clusters.ts `DEPLOYMENTS`). Its program id
// is the app's `PROGRAM_ID`, and the same entry is compiled into the worker
// bundle as the fee branch's scope (worker-entry.ts `setFeeScope`). Nothing
// here derives the scope from the RPC URL or from any RPC answer: the
// network check below only compares the RPC's genesis hash with the entry's
// tag. No DOM dependency, so the worker entry can import it too; its SDK
// imports are subpaths, not the main-thread barrel, so the worker bundle
// stays small.

import { address } from '@solana/kit';
import { type Deployment, deployment } from '@thevault/sdk/clusters';
import { addressBytes } from '@thevault/sdk/vault';
import type { Rpc } from './rpc';

/** The entry's name as built; `deployment()` below throws at module load for any name the table does not hold. */
export const DEPLOYMENT_NAME: string = import.meta.env.VITE_DEPLOYMENT ?? 'mainnet';
export const DEPLOYMENT: Deployment = deployment(DEPLOYMENT_NAME);

/** The vault program this build signs for: the entry's, never a separate setting (2.5, "One selection per build"). */
export const PROGRAM_ID_B58: string = DEPLOYMENT.programId;
export const PROGRAM_ID: Uint8Array = addressBytes(address(PROGRAM_ID_B58));
/** The entry's cluster tag, base58: a public cluster's genesis hash, or the shim's fixed test tag. */
export const CLUSTER_TAG: string = DEPLOYMENT.genesisHash;

export const WRONG_NETWORK_SENTENCE = 'This network is not the one this app was built for. Check the RPC in Settings.';

/** The selected RPC is not on this build's network, or its genesis hash could not be read (2.5). `detail` says which, for logs and evidence; the message is the sentence screens show. */
export class WrongNetwork extends Error {
  constructor(readonly detail?: string) {
    super(WRONG_NETWORK_SENTENCE);
    this.name = 'WrongNetwork';
  }
}

/**
 * The network check of 2.5: reads `getGenesisHash` from `rpc` and compares it
 * with the entry's tag. A mismatch or a failed read throws `WrongNetwork`.
 * The answer is only compared, never used as a derivation input. Read per
 * call and never cached, like the program-hash gate (R42); unlike that gate,
 * it is not skipped in development mode.
 */
export async function assertDeployment(rpc: Pick<Rpc, 'getGenesisHash'>): Promise<void> {
  let hash: string;
  try {
    hash = await rpc.getGenesisHash();
  } catch (e) {
    throw new WrongNetwork(`the genesis hash could not be read: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (hash !== CLUSTER_TAG) throw new WrongNetwork(`the RPC's genesis hash is ${String(hash)}, not ${CLUSTER_TAG} (${DEPLOYMENT.name})`);
}
